import React, { useState } from 'react';
import { X, Globe, FileText, Ship, Plane, BookOpen, Building2, Search, MapPin, Phone, Mail, ExternalLink } from 'lucide-react';

// WhatsApp Business number for freight inquiries
const WHATSAPP_NUMBER = "+61452401418";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER.replace('+', '')}?text=${encodeURIComponent(
  "Hi! I have a question about Pacific Island trade. Can you help me with:\n\n• Shipping rates and quotes\n• HS Codes lookup\n• Export documentation\n• Trade regulations"
)}`;

// HS Code Quick Lookup URL (links to WhatsApp with HS Code query)
const getHSCodeWhatsAppUrl = (query) => `https://wa.me/${WHATSAPP_NUMBER.replace('+', '')}?text=${encodeURIComponent(
  `Hi! I need help finding the HS Code for:\n\n${query}\n\nCan you assist with the correct classification?`
)}`;

// Pacific Island Trade & Commerce Bodies - Comprehensive List
const PACIFIC_TRADE_BODIES = [
  {
    country: "Fiji",
    bodies: [
      { name: "Investment Fiji", url: "https://www.investmentfiji.org.fj/", type: "Investment & Trade" },
      { name: "Fiji Revenue & Customs Service", url: "https://www.frcs.org.fj/", type: "Customs & HS Codes" },
      { name: "Ministry of Trade & Commerce", url: "https://www.mcttt.gov.fj/", type: "Government" },
    ],
    flag: "🇫🇯"
  },
  {
    country: "Papua New Guinea",
    bodies: [
      { name: "Investment Promotion Authority", url: "https://www.ipa.gov.pg/", type: "Investment & Trade" },
      { name: "PNG Customs Service", url: "https://www.customs.gov.pg/", type: "Customs & HS Codes" },
      { name: "Dept of Commerce & Industry", url: "https://www.dci.gov.pg/", type: "Government" },
    ],
    flag: "🇵🇬"
  },
  {
    country: "Samoa",
    bodies: [
      { name: "Ministry of Commerce, Industry & Labour", url: "https://www.mcil.gov.ws/", type: "Government" },
      { name: "Samoa Chamber of Commerce", url: "https://www.samoachamber.ws/", type: "Chamber" },
      { name: "Samoa Customs", url: "https://www.revenue.gov.ws/", type: "Customs & HS Codes" },
    ],
    flag: "🇼🇸"
  },
  {
    country: "Tonga",
    bodies: [
      { name: "Ministry of Trade & Economic Development", url: "https://www.mted.gov.to/", type: "Government" },
      { name: "Tonga Chamber of Commerce", url: "https://www.tongachamber.org/", type: "Chamber" },
      { name: "Tonga Customs & Revenue", url: "https://www.revenue.gov.to/", type: "Customs & HS Codes" },
    ],
    flag: "🇹🇴"
  },
  {
    country: "Vanuatu",
    bodies: [
      { name: "Vanuatu Investment Promotion Authority", url: "https://www.investvanuatu.org/", type: "Investment & Trade" },
      { name: "Vanuatu Chamber of Commerce", url: "https://www.vcci.com.vu/", type: "Chamber" },
      { name: "Dept of Customs & Inland Revenue", url: "https://customsinlandrevenue.gov.vu/", type: "Customs & HS Codes" },
    ],
    flag: "🇻🇺"
  },
  {
    country: "Solomon Islands",
    bodies: [
      { name: "Solomon Islands Chamber of Commerce", url: "https://www.sicci.com.sb/", type: "Chamber" },
      { name: "Ministry of Commerce", url: "https://www.commerce.gov.sb/", type: "Government" },
      { name: "Customs & Excise Division", url: "https://www.customs.gov.sb/", type: "Customs & HS Codes" },
    ],
    flag: "🇸🇧"
  },
  {
    country: "Kiribati",
    bodies: [
      { name: "Ministry of Commerce, Industry & Cooperatives", url: "https://www.mcic.gov.ki/", type: "Government" },
      { name: "Kiribati Chamber of Commerce", url: "https://www.kncci.org.ki/", type: "Chamber" },
    ],
    flag: "🇰🇮"
  },
  {
    country: "Marshall Islands",
    bodies: [
      { name: "Marshall Islands Marine Resources Authority", url: "https://www.mimra.com/", type: "Trade Authority" },
      { name: "Office of Commerce & Investment", url: "https://www.rmiocia.org/", type: "Investment & Trade" },
    ],
    flag: "🇲🇭"
  },
  {
    country: "Micronesia (FSM)",
    bodies: [
      { name: "FSM Dept of Resources & Development", url: "https://www.fsmrd.fm/", type: "Government" },
      { name: "National Fisheries Corporation", url: "https://www.fsmpio.fm/", type: "Trade Authority" },
    ],
    flag: "🇫🇲"
  },
  {
    country: "Palau",
    bodies: [
      { name: "Palau Chamber of Commerce", url: "https://www.palauchambersofcommerce.com/", type: "Chamber" },
      { name: "Bureau of Revenue & Taxation", url: "https://www.palaugov.pw/", type: "Customs & HS Codes" },
    ],
    flag: "🇵🇼"
  },
  {
    country: "Nauru",
    bodies: [
      { name: "Nauru Chamber of Commerce", url: "https://www.naurugov.nr/", type: "Chamber" },
      { name: "Dept of Commerce, Industry & Environment", url: "https://www.naurugov.nr/", type: "Government" },
    ],
    flag: "🇳🇷"
  },
  {
    country: "Tuvalu",
    bodies: [
      { name: "Ministry of Finance & Economic Development", url: "https://www.tuvalugov.tv/", type: "Government" },
    ],
    flag: "🇹🇻"
  },
  {
    country: "Cook Islands",
    bodies: [
      { name: "Cook Islands Chamber of Commerce", url: "https://www.cicoc.org.ck/", type: "Chamber" },
      { name: "Business Trade Investment Board", url: "https://www.btib.gov.ck/", type: "Investment & Trade" },
    ],
    flag: "🇨🇰"
  },
  {
    country: "New Caledonia",
    bodies: [
      { name: "CCI Nouvelle-Calédonie", url: "https://www.cci.nc/", type: "Chamber" },
      { name: "Customs New Caledonia", url: "https://www.douane.gouv.nc/", type: "Customs & HS Codes" },
    ],
    flag: "🇳🇨"
  },
  {
    country: "French Polynesia",
    bodies: [
      { name: "CCISM - Chamber of Commerce", url: "https://www.ccism.pf/", type: "Chamber" },
      { name: "Customs French Polynesia", url: "https://www.douane.gouv.fr/", type: "Customs & HS Codes" },
    ],
    flag: "🇵🇫"
  },
];

// Regional Trade Organizations
const REGIONAL_ORGANIZATIONS = [
  { name: "Pacific Islands Forum Secretariat", url: "https://www.forumsec.org/", description: "Regional cooperation & trade policy" },
  { name: "Pacific Community (SPC)", url: "https://www.spc.int/", description: "Technical assistance & development" },
  { name: "Pacific Trade Invest", url: "https://www.pacifictradeinvest.com/", description: "Export promotion & market access" },
  { name: "Pacific Islands Trade & Investment Commission", url: "https://www.pitic.org.au/", description: "Trade facilitation Australia" },
  { name: "PACER Plus", url: "https://www.pacerplus.org/", description: "Pacific trade agreement" },
  { name: "Melanesian Spearhead Group", url: "https://www.msgsec.info/", description: "Melanesian trade cooperation" },
];

// Export Documentation Resources
const EXPORT_DOCS = [
  { name: "Export Documentation Guide", url: "https://www.trade.gov/export-documentation", icon: FileText },
  { name: "Commercial Invoice Template", url: "https://www.trade.gov/commercial-invoice", icon: FileText },
  { name: "Packing List Guide", url: "https://www.trade.gov/packing-list", icon: FileText },
  { name: "Certificate of Origin Info", url: "https://www.trade.gov/certificate-origin", icon: FileText },
  { name: "Phytosanitary Certificate Guide", url: "https://www.fao.org/3/y4838e/y4838e0a.htm", icon: FileText },
  { name: "Bill of Lading Explained", url: "https://www.freightos.com/freight-resources/bill-of-lading/", icon: FileText },
];

// HS Code Resources
const HS_CODE_RESOURCES = [
  { name: "World Customs Organization", url: "https://www.wcoomd.org/en/topics/nomenclature.aspx", icon: Globe, description: "Official HS Nomenclature" },
  { name: "HS Code Lookup (USA)", url: "https://hts.usitc.gov/", icon: Search, description: "US Harmonized Tariff" },
  { name: "HS Code Lookup (Australia)", url: "https://www.abf.gov.au/importing-exporting-and-manufacturing/tariff-classification", icon: Search, description: "Australian Tariff" },
  { name: "HS Code Lookup (NZ)", url: "https://www.customs.govt.nz/business/tariffs/", icon: Search, description: "New Zealand Tariff" },
  { name: "HS Code Lookup (EU)", url: "https://taxation-customs.ec.europa.eu/online-services/online-services-and-databases-customs/hs-eu-classification_en", icon: Search, description: "European Union" },
];

// Freight Resources
const FREIGHT_RESOURCES = [
  { name: "Incoterms 2020 Guide", url: "https://iccwbo.org/business-solutions/incoterms-rules/", icon: BookOpen },
  { name: "Sea Freight Info", url: "https://www.freightos.com/freight-resources/", icon: Ship },
  { name: "Air Freight Guide", url: "https://www.iata.org/en/programs/cargo/", icon: Plane },
  { name: "Container Specifications", url: "https://www.hapag-lloyd.com/en/services-information/cargo-fleet/container.html", icon: Ship },
];

// Common Pacific Export Products with HS Codes
const COMMON_PACIFIC_HS_CODES = [
  { product: "Vanilla Beans", hsCode: "0905.10", description: "Neither crushed nor ground" },
  { product: "Cocoa Beans", hsCode: "1801.00", description: "Whole or broken, raw or roasted" },
  { product: "Coffee (Green)", hsCode: "0901.11", description: "Not roasted, not decaffeinated" },
  { product: "Coconut Oil", hsCode: "1513.11", description: "Crude coconut (copra) oil" },
  { product: "Kava", hsCode: "1211.90", description: "Plants for pharmacy/perfume use" },
  { product: "Taro", hsCode: "0714.40", description: "Fresh, chilled, frozen or dried" },
  { product: "Noni Juice", hsCode: "2009.89", description: "Juice of other fruits/vegetables" },
  { product: "Sandalwood Oil", hsCode: "3301.29", description: "Essential oils" },
  { product: "Tuna (Fresh)", hsCode: "0302.32", description: "Yellowfin tuna, fresh or chilled" },
  { product: "Sea Cucumber", hsCode: "0308.19", description: "Frozen sea cucumbers" },
  { product: "Timber (Logs)", hsCode: "4403.49", description: "Tropical wood in the rough" },
  { product: "Palm Oil", hsCode: "1511.10", description: "Crude palm oil" },
];

const WhatsAppFreightWidget = () => {
  const [showOptions, setShowOptions] = useState(false);
  const [activeTab, setActiveTab] = useState('chat');
  const [hsCodeSearch, setHsCodeSearch] = useState('');
  const [expandedCountry, setExpandedCountry] = useState(null);

  const filteredHSCodes = COMMON_PACIFIC_HS_CODES.filter(item => 
    item.product.toLowerCase().includes(hsCodeSearch.toLowerCase()) ||
    item.hsCode.includes(hsCodeSearch) ||
    item.description.toLowerCase().includes(hsCodeSearch.toLowerCase())
  );

  return (
    <div className="fixed bottom-6 right-6 z-50" data-testid="whatsapp-freight-widget">
      {/* Options Popup */}
      {showOptions && (
        <div className="absolute bottom-16 right-0 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden w-[420px] animate-in fade-in slide-in-from-bottom-5 duration-200">
          <div className="bg-gradient-to-r from-[#25D366] to-[#128C7E] p-4 text-white">
            <div className="flex items-center justify-between">
              <h4 className="font-chivo font-semibold">Pacific Trade & Export Help</h4>
              <button 
                onClick={() => setShowOptions(false)}
                className="hover:bg-white/20 rounded-full p-1 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-sm text-white/90 mt-1">HS Codes, Trade Bodies & Live Support</p>
          </div>
          
          {/* Tab Navigation */}
          <div className="flex border-b border-slate-200 overflow-x-auto">
            <button 
              onClick={() => setActiveTab('chat')}
              className={`flex-1 py-2 text-xs font-medium transition-colors whitespace-nowrap px-2 ${activeTab === 'chat' ? 'text-[#25D366] border-b-2 border-[#25D366]' : 'text-slate-500'}`}
            >
              💬 Chat
            </button>
            <button 
              onClick={() => setActiveTab('hscodes')}
              className={`flex-1 py-2 text-xs font-medium transition-colors whitespace-nowrap px-2 ${activeTab === 'hscodes' ? 'text-[#25D366] border-b-2 border-[#25D366]' : 'text-slate-500'}`}
            >
              🔍 HS Codes
            </button>
            <button 
              onClick={() => setActiveTab('pacific')}
              className={`flex-1 py-2 text-xs font-medium transition-colors whitespace-nowrap px-2 ${activeTab === 'pacific' ? 'text-[#25D366] border-b-2 border-[#25D366]' : 'text-slate-500'}`}
            >
              🌴 Pacific Trade
            </button>
            <button 
              onClick={() => setActiveTab('docs')}
              className={`flex-1 py-2 text-xs font-medium transition-colors whitespace-nowrap px-2 ${activeTab === 'docs' ? 'text-[#25D366] border-b-2 border-[#25D366]' : 'text-slate-500'}`}
            >
              📄 Docs
            </button>
          </div>
          
          <div className="p-4 max-h-96 overflow-y-auto">
            {/* Chat Tab */}
            {activeTab === 'chat' && (
              <div className="space-y-3">
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-chat-btn"
                  className="flex items-center gap-3 w-full p-3 rounded-xl bg-[#25D366] text-white hover:bg-[#128C7E] transition-all"
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <div className="text-left">
                    <span className="font-medium block">Chat on WhatsApp</span>
                    <span className="text-xs text-white/80">Get instant trade help</span>
                  </div>
                </a>
                
                <div className="border-t border-slate-100 pt-3">
                  <p className="text-xs text-slate-500 mb-2 font-medium">WE CAN HELP WITH</p>
                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                    <div className="flex items-center gap-1"><Search className="w-3 h-3 text-green-500" /> HS Code Lookup</div>
                    <div className="flex items-center gap-1"><Ship className="w-3 h-3 text-blue-500" /> Freight Quotes</div>
                    <div className="flex items-center gap-1"><FileText className="w-3 h-3 text-amber-500" /> Export Documents</div>
                    <div className="flex items-center gap-1"><Globe className="w-3 h-3 text-purple-500" /> Trade Regulations</div>
                    <div className="flex items-center gap-1"><Building2 className="w-3 h-3 text-red-500" /> Customs Clearance</div>
                    <div className="flex items-center gap-1"><MapPin className="w-3 h-3 text-cyan-500" /> Pacific Trade Info</div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-lg p-3 mt-2">
                  <p className="text-xs font-medium text-slate-700 mb-1">📞 Support Hours</p>
                  <p className="text-xs text-slate-600">Mon-Fri: 8am - 6pm (AEST)</p>
                  <p className="text-xs text-slate-600">Response within 2 hours</p>
                </div>
              </div>
            )}

            {/* HS Codes Tab */}
            {activeTab === 'hscodes' && (
              <div className="space-y-3">
                {/* Search Box */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search product or HS code..."
                    value={hsCodeSearch}
                    onChange={(e) => setHsCodeSearch(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                {/* Quick HS Code Reference */}
                <p className="text-xs text-slate-500 font-medium">COMMON PACIFIC EXPORT HS CODES</p>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {filteredHSCodes.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                      <div>
                        <span className="text-sm font-medium text-slate-700">{item.product}</span>
                        <p className="text-xs text-slate-500">{item.description}</p>
                      </div>
                      <span className="text-sm font-mono font-bold text-green-600 bg-green-50 px-2 py-1 rounded">{item.hsCode}</span>
                    </div>
                  ))}
                </div>

                {/* Ask via WhatsApp */}
                <a
                  href={getHSCodeWhatsAppUrl(hsCodeSearch || "my product")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full p-2 rounded-lg bg-[#25D366] text-white hover:bg-[#128C7E] transition-all text-sm"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Can't find your HS Code? Ask us!
                </a>

                {/* HS Code Resources */}
                <p className="text-xs text-slate-500 font-medium mt-3">OFFICIAL HS CODE DATABASES</p>
                {HS_CODE_RESOURCES.map((resource, idx) => (
                  <a
                    key={idx}
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors group"
                  >
                    <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center group-hover:bg-amber-100 transition-colors">
                      <resource.icon className="w-4 h-4 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <span className="text-sm text-slate-700 group-hover:text-ocean-deep block">{resource.name}</span>
                      <span className="text-xs text-slate-500">{resource.description}</span>
                    </div>
                    <ExternalLink className="w-3 h-3 text-slate-400" />
                  </a>
                ))}
              </div>
            )}
            
            {/* Pacific Trade Bodies Tab */}
            {activeTab === 'pacific' && (
              <div className="space-y-3">
                {/* Regional Organizations */}
                <p className="text-xs text-slate-500 font-medium">🌏 REGIONAL TRADE ORGANIZATIONS</p>
                <div className="space-y-2 mb-4">
                  {REGIONAL_ORGANIZATIONS.map((org, idx) => (
                    <a
                      key={idx}
                      href={org.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-2 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors group"
                    >
                      <Building2 className="w-4 h-4 text-blue-600" />
                      <div className="flex-1">
                        <span className="text-sm text-slate-700 font-medium block">{org.name}</span>
                        <span className="text-xs text-slate-500">{org.description}</span>
                      </div>
                      <ExternalLink className="w-3 h-3 text-slate-400" />
                    </a>
                  ))}
                </div>

                {/* Country-specific Trade Bodies */}
                <p className="text-xs text-slate-500 font-medium">🏛️ NATIONAL TRADE & COMMERCE BODIES</p>
                <div className="space-y-1">
                  {PACIFIC_TRADE_BODIES.map((country, idx) => (
                    <div key={idx} className="border border-slate-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => setExpandedCountry(expandedCountry === idx ? null : idx)}
                        className="w-full flex items-center justify-between p-2 hover:bg-slate-50 transition-colors"
                      >
                        <span className="flex items-center gap-2">
                          <span className="text-lg">{country.flag}</span>
                          <span className="text-sm font-medium text-slate-700">{country.country}</span>
                        </span>
                        <span className="text-xs text-slate-400">{country.bodies.length} links</span>
                      </button>
                      {expandedCountry === idx && (
                        <div className="bg-slate-50 p-2 space-y-1 border-t border-slate-200">
                          {country.bodies.map((body, bodyIdx) => (
                            <a
                              key={bodyIdx}
                              href={body.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center justify-between p-2 rounded bg-white hover:bg-green-50 transition-colors text-sm"
                            >
                              <div>
                                <span className="text-slate-700 block">{body.name}</span>
                                <span className="text-xs text-slate-500">{body.type}</span>
                              </div>
                              <ExternalLink className="w-3 h-3 text-slate-400" />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Export Documentation Tab */}
            {activeTab === 'docs' && (
              <div className="space-y-3">
                <p className="text-xs text-slate-500 font-medium">📋 EXPORT DOCUMENTATION</p>
                {EXPORT_DOCS.map((doc, idx) => (
                  <a
                    key={idx}
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`export-doc-${idx}`}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors group"
                  >
                    <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                      <doc.icon className="w-4 h-4 text-blue-600" />
                    </div>
                    <span className="text-sm text-slate-700 group-hover:text-ocean-deep flex-1">{doc.name}</span>
                    <ExternalLink className="w-3 h-3 text-slate-400" />
                  </a>
                ))}
                
                <p className="text-xs text-slate-500 font-medium mt-4">🚢 FREIGHT & SHIPPING GUIDES</p>
                {FREIGHT_RESOURCES.map((resource, idx) => (
                  <a
                    key={idx}
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`resource-link-${idx}`}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors group"
                  >
                    <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center group-hover:bg-amber-100 transition-colors">
                      <resource.icon className="w-4 h-4 text-amber-600" />
                    </div>
                    <span className="text-sm text-slate-700 group-hover:text-ocean-deep flex-1">{resource.name}</span>
                    <ExternalLink className="w-3 h-3 text-slate-400" />
                  </a>
                ))}
                
                <div className="bg-green-50 rounded-lg p-3 mt-3">
                  <p className="text-xs font-medium text-green-800 mb-1">Need personalized help?</p>
                  <a 
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-green-600 hover:underline flex items-center gap-1"
                  >
                    Chat with our trade experts on WhatsApp <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Main WhatsApp Button */}
      <button
        onClick={() => setShowOptions(!showOptions)}
        data-testid="whatsapp-freight-btn"
        className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 ${
          showOptions 
            ? 'bg-slate-600 rotate-0' 
            : 'bg-[#25D366] hover:bg-[#128C7E] hover:scale-110 hover:shadow-xl'
        }`}
      >
        {showOptions ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        )}
      </button>
    </div>
  );
};

export default WhatsAppFreightWidget;
